import React, { useContext, useMemo, useState } from 'react';

import { ViewerContext } from 'common/containers/ViewerContainer';
import FileRenderer from 'common/file/FileRenderer';
import { createFiles, createImageFiles } from 'common/file/utils/createFiles';
import translateString from 'common/i18n/translateString';
import Markdown from 'common/markdown/Markdown';
import ModernConfirmModal from 'common/modals/ModernConfirmModal';
import CommentComposer from 'common/subdomain/admin/v2/AdminV2IdeaSheet/InternalView/Activity/CommentComposer';
import { useDeleteIdeaComment } from 'common/subdomain/admin/v2/hooks/useIdeaCommentMutations';
import Timestamp from 'common/Timestamp';
import Flex from 'common/ui/Flex';
import VStack from 'common/ui/VStack';
import Button, { type ButtonProps } from 'common/uiV3/Button';
import UserLockupV2 from 'common/user/UserLockupV2';

import type { QueryParams as IdeaActivityQueryParams } from 'common/actions/ideaActivityQueries';
import type { Idea } from 'common/api/endpoints/idea';
import type { IdeaComment as IdeaCommentType } from 'common/api/endpoints/ideaActivity';

type IdeaCommentProps = {
  activityQueryParams: IdeaActivityQueryParams;
  comment: IdeaCommentType;
  idea: Idea;
};

const IdeaComment = ({ activityQueryParams, idea, comment }: IdeaCommentProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const files = useMemo(() => {
    return [...createImageFiles(comment.imageURLs || []), ...createFiles(comment.files || [])];
  }, [comment.imageURLs, comment.files]);

  const hasFiles = comment.imageURLs?.length > 0 || comment.files?.length > 0;

  return (
    <VStack className="gap-1">
      <UserLockupV2 user={comment.author} />
      <VStack className="ml-10 gap-1">
        {isEditing ? (
          <CommentComposer
            idea={idea}
            autoFocus
            showAvatar={false}
            showCancel
            comment={comment}
            activityQueryParams={activityQueryParams}
            initialMentionedUsers={comment.mentionedUsers}
            onCancel={() => setIsEditing(false)}
            onSaved={() => setIsEditing(false)}
            className="base-border rounded-md p-3"
          />
        ) : (
          <>
            <IdeaCommentBody comment={comment} />
            {hasFiles && <FileRenderer allowRemove={false} files={files} />}
            <IdeaCommentFooter
              comment={comment}
              activityQueryParams={activityQueryParams}
              onEdit={() => setIsEditing(true)}
            />
          </>
        )}
      </VStack>
    </VStack>
  );
};

// TODO: Markdown V2/Mentionify so we dont need to style span (mention) like this.
const IdeaCommentBody = ({ comment }: Omit<IdeaCommentProps, 'idea' | 'activityQueryParams'>) => {
  return (
    <div className="[&_span]:text-base [&_span]:px-1 [&_span]:font-medium [&_span]:rounded-[2px]">
      <Markdown
        contents={translateString(comment, 'value', true)}
        mentions={comment.mentionedUsers}
      />
    </div>
  );
};

const IdeaCommentFooter = ({
  comment,
  onEdit,
  activityQueryParams,
}: Omit<IdeaCommentProps, 'idea'> & { onEdit: () => void }) => {
  return (
    <Flex className="gap-1 text-sm text-muted-foreground">
      <Timestamp timestamp={comment.created} />
      <EditIdeaCommentButton comment={comment} onClick={onEdit} />
      <DeleteIdeaCommentButton comment={comment} activityQueryParams={activityQueryParams} />
    </Flex>
  );
};

const EditIdeaCommentButton = ({
  comment,
  ...props
}: Omit<IdeaCommentProps, 'idea' | 'activityQueryParams'> & ButtonProps) => {
  const viewer = useContext(ViewerContext);
  const isViewerAuthor = viewer._id === comment.author._id;

  if (!isViewerAuthor) {
    return null;
  }

  return (
    <>
      <IdeaCommmentFooterSeparator />
      <Button
        variant="unstyled"
        className="text-sm text-muted-foreground hover:text-foreground"
        {...props}>
        Edit
      </Button>
    </>
  );
};

const DeleteIdeaCommentButton = ({
  comment,
  activityQueryParams,
}: Omit<IdeaCommentProps, 'idea'>) => {
  const viewer = useContext(ViewerContext);
  const isViewerAuthor = viewer._id === comment.author._id;

  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);

  const deleteComment = useDeleteIdeaComment(activityQueryParams);

  if (!isViewerAuthor) {
    return null;
  }

  return (
    <>
      <IdeaCommmentFooterSeparator />
      <Button
        onClick={() => setShowConfirmDeleteModal(true)}
        variant="unstyled"
        className="text-sm text-muted-foreground hover:text-foreground">
        Delete
      </Button>
      {showConfirmDeleteModal && (
        <ModernConfirmModal
          onClose={() => setShowConfirmDeleteModal(false)}
          onConfirm={() => deleteComment.mutate({ commentID: comment._id })}
          loading={deleteComment.isLoading}
          confirmText="Delete"
          type="destructive"
          header="Delete comment">
          <p>Are you sure you'd like to delete this comment?</p>
        </ModernConfirmModal>
      )}
    </>
  );
};

const IdeaCommmentFooterSeparator = () => {
  return <span className="mx-1">·</span>;
};

export default IdeaComment;
